import React, { useState, useEffect, useRef } from 'react';
import { Briefcase, Plus, Search, Edit2, Trash2, ExternalLink, Loader2, X, CheckCircle2, Upload, LayoutGrid, Zap, Code, Smartphone, Brain, MessageSquare, Globe, Tag, Image as ImageIcon, Video } from 'lucide-react';
import { apiFetch } from '../../utils/api';
import Swal from 'sweetalert2';

interface PortfolioItem {
    id: string;
    title: string;
    category: string;
    description: string;
    image_url: string;
    video_url?: string;
    demo_url?: string;
    created_at?: string;
    updated_at?: string;
}

const DEFAULT_PRESET_IMAGES = [
    { name: 'Sadguru Tyres', path: '/images/portfolio/SadguruTyres.png' },
    { name: 'House of Urvaah', path: '/images/portfolio/HouseOfUrvaah.png' },
    { name: 'Royal Thread App', path: '/images/portfolio/RoyalThread.png' },
    { name: 'Capitaline Database', path: '/images/portfolio/Capitaline.png' },
    { name: 'Jupiter Tatravagonka', path: '/images/portfolio/Jupitor.png' },
    { name: 'Power Platform', path: '/images/portfolio/PowerBI.png' },
    { name: 'Mobile Apps', path: '/images/portfolio/MobileApp.png' },
    { name: 'Jenkins CI/CD', path: '/images/portfolio/Jenkins.png' },
];

const PRESET_CATEGORIES = [
    'React',
    'Power Platform',
    'Flutter',
    'AI/ML',
    'Chatbot',
    'WordPress',
    'Other'
];

const getCategoryIcon = (category: string, active: boolean = false) => {
    const cat = category.toLowerCase();
    if (cat === 'all') {
        return <LayoutGrid className={`w-4 h-4 shrink-0 ${active ? 'text-slate-200' : 'text-slate-500'}`} />;
    }
    if (cat.includes('react')) {
        return <Code className={`w-4 h-4 shrink-0 ${active ? 'text-blue-200' : 'text-blue-500'}`} />;
    }
    if (cat.includes('power')) {
        return <Zap className={`w-4 h-4 shrink-0 ${active ? 'text-amber-200' : 'text-amber-500'}`} />;
    }
    if (cat.includes('flutter')) {
        return <Smartphone className={`w-4 h-4 shrink-0 ${active ? 'text-cyan-200' : 'text-cyan-500'}`} />;
    }
    if (cat.includes('ai') || cat.includes('ml')) {
        return <Brain className={`w-4 h-4 shrink-0 ${active ? 'text-purple-200' : 'text-purple-500'}`} />;
    }
    if (cat.includes('bot') || cat.includes('chatbot')) {
        return <MessageSquare className={`w-4 h-4 shrink-0 ${active ? 'text-emerald-200' : 'text-emerald-500'}`} />;
    }
    if (cat.includes('wordpress')) {
        return <Globe className={`w-4 h-4 shrink-0 ${active ? 'text-blue-200' : 'text-blue-600'}`} />;
    }
    return <Tag className={`w-4 h-4 shrink-0 ${active ? 'text-slate-200' : 'text-slate-500'}`} />;
};

const resolvePortfolioImage = (title: string = '', imageUrl: string = ''): string => {
    if (imageUrl && !imageUrl.startsWith('/static/media/') && imageUrl.trim() !== '') {
        return imageUrl;
    }
    const lower = title.toLowerCase();
    if (lower.includes('sadguru') || lower.includes('tyre')) return '/images/portfolio/SadguruTyres.png';
    if (lower.includes('urvaah')) return '/images/portfolio/HouseOfUrvaah.png';
    if (lower.includes('capitaline')) return '/images/portfolio/Capitaline.png';
    if (lower.includes('jupiter')) return '/images/portfolio/Jupitor.png';
    if (lower.includes('power')) return '/images/portfolio/PowerBI.png';
    if (lower.includes('mobile')) return '/images/portfolio/MobileApp.png';
    if (lower.includes('jenkins')) return '/images/portfolio/Jenkins.png';
    return imageUrl || '/images/portfolio/Capitaline.png';
};

const isVideoFile = (url: string = ''): boolean => {
    if (!url) return false;
    const lower = url.toLowerCase();
    return lower.endsWith('.mp4') || lower.endsWith('.webm') || lower.endsWith('.mov') || lower.endsWith('.ogg') || lower.startsWith('data:video/');
};

const PortfolioManagement: React.FC = () => {
    const [items, setItems] = useState<PortfolioItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState<PortfolioItem | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const imageInputRef = useRef<HTMLInputElement>(null);
    const videoInputRef = useRef<HTMLInputElement>(null);

    const [formData, setFormData] = useState({
        title: '',
        category: 'Web Application',
        description: '',
        image_url: '',
        video_url: '',
        demo_url: ''
    });

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
            if (file.size > 10 * 1024 * 1024) {
                Swal.fire(
                    'Image File Too Large',
                    `Selected image is ${sizeMB}MB. Please select an image file smaller than 10MB for optimal performance.`,
                    'warning'
                );
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                if (reader.result) {
                    setFormData(prev => ({ ...prev, image_url: reader.result as string }));
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const handleVideoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
            if (file.size > 10 * 1024 * 1024) {
                Swal.fire(
                    'Video Exceeds Direct API Limit',
                    `Selected video is ${sizeMB}MB. Due to server API payload limits, direct video uploads must be under 10MB. Please compress your video under 10MB or paste a direct Video URL link below.`,
                    'warning'
                );
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                if (reader.result) {
                    setFormData(prev => ({ ...prev, video_url: reader.result as string }));
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const fetchPortfolio = async () => {
        setLoading(true);
        try {
            const res = await apiFetch('/portfolio');
            if (res.ok) {
                const data = await res.json();
                setItems(data);
            }
        } catch (err) {
            console.error('Failed to load portfolio items:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPortfolio();
    }, []);

    const handleOpenModal = (item?: PortfolioItem) => {
        if (item) {
            setEditingItem(item);
            setFormData({
                title: item.title,
                category: item.category,
                description: item.description,
                image_url: item.image_url || '',
                video_url: item.video_url || '',
                demo_url: item.demo_url || ''
            });
        } else {
            setEditingItem(null);
            setFormData({
                title: '',
                category: 'Web Application',
                description: '',
                image_url: '',
                video_url: '',
                demo_url: ''
            });
        }
        setShowModal(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            const url = editingItem ? `/portfolio/${editingItem.id}` : '/portfolio';
            const method = editingItem ? 'PUT' : 'POST';

            const res = await apiFetch(url, {
                method,
                body: JSON.stringify(formData)
            });

            if (res.ok) {
                Swal.fire({
                    icon: 'success',
                    title: editingItem ? 'Updated!' : 'Created!',
                    text: `Portfolio project ${editingItem ? 'updated' : 'added'} successfully.`,
                    timer: 2000,
                    showConfirmButton: false
                });
                setShowModal(false);
                fetchPortfolio();
            } else {
                if (res.status === 413) {
                    Swal.fire('Payload Limit Exceeded (413)', 'The file is too large for the server API payload limit. Please upload a video/image under 10MB or paste a direct Video URL link.', 'error');
                } else {
                    try {
                        const errData = await res.json();
                        Swal.fire('Error', errData.message || 'Failed to save portfolio item', 'error');
                    } catch {
                        Swal.fire('Error', `Server returned error status ${res.status}`, 'error');
                    }
                }
            }
        } catch (err) {
            Swal.fire('Error', 'Unable to connect to server', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id: string, title: string) => {
        const confirm = await Swal.fire({
            title: 'Delete Project?',
            text: `Are you sure you want to delete "${title}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Yes, delete it!'
        });

        if (confirm.isConfirmed) {
            try {
                const res = await apiFetch(`/portfolio/${id}`, { method: 'DELETE' });
                if (res.ok) {
                    Swal.fire('Deleted!', 'Portfolio project has been deleted.', 'success');
                    fetchPortfolio();
                } else {
                    Swal.fire('Error', 'Failed to delete project.', 'error');
                }
            } catch (err) {
                Swal.fire('Error', 'Unable to connect to server', 'error');
            }
        }
    };

    const categories = ['All', 'React', 'Power Platform', 'Flutter', 'AI/ML', 'Chatbot', 'WordPress', 'Other'];

    const filteredItems = items.filter(project => {
        const projectCat = (project.category || '').toLowerCase();
        const projectTitle = (project.title || '').toLowerCase();
        const projectDesc = (project.description || '').toLowerCase();

        const matchesReact = (projectCat.includes('react') || projectTitle.includes('react')) && !projectCat.includes('native') && !projectTitle.includes('native');
        const matchesPowerPlatform = projectCat.includes('power') || projectTitle.includes('power');
        const matchesFlutter = projectCat.includes('flutter') || projectTitle.includes('flutter');
        const matchesChatbot = projectCat.includes('bot') || projectCat.includes('chat') || projectCat.includes('assistant') || projectTitle.includes('bot') || projectTitle.includes('chat') || projectTitle.includes('assistant') || projectDesc.includes('chatbot') || projectDesc.includes('chat bot') || projectDesc.includes('ai assistant');
        const matchesAIML = (projectCat.includes('ai') || projectCat.includes('ml') || projectTitle.includes('ai') || projectTitle.includes('ml') || projectTitle.includes('ai-powered')) && !matchesChatbot;
        const matchesWordPress = projectCat.includes('wordpress') || projectTitle.includes('wordpress') || projectDesc.includes('wordpress');

        const matchesAnySpecificCategory = matchesReact || matchesPowerPlatform || matchesFlutter || matchesAIML || matchesChatbot || matchesWordPress;

        if (selectedCategory !== 'All') {
            const cat = selectedCategory.toLowerCase();
            if (cat === 'react') {
                if (!matchesReact) return false;
            } else if (cat === 'power platform') {
                if (!matchesPowerPlatform) return false;
            } else if (cat === 'flutter') {
                if (!matchesFlutter) return false;
            } else if (cat === 'ai/ml' || cat === 'ai & ml') {
                if (!matchesAIML && !projectCat.includes('ai')) return false;
            } else if (cat === 'chatbot') {
                if (!matchesChatbot && !projectCat.includes('chat') && !projectCat.includes('bot')) return false;
            } else if (cat === 'wordpress') {
                if (!matchesWordPress) return false;
            } else if (cat === 'other') {
                if (matchesAnySpecificCategory) return false;
            } else {
                if (!projectCat.includes(cat) && !projectTitle.includes(cat) && !projectDesc.includes(cat)) return false;
            }
        }

        if (searchQuery.trim() !== '') {
            const q = searchQuery.toLowerCase().trim();
            const matchesTitle = projectTitle.includes(q);
            const matchesCategory = projectCat.includes(q);
            const matchesDesc = projectDesc.includes(q);
            if (!matchesTitle && !matchesCategory && !matchesDesc) return false;
        }

        return true;
    });

    return (
        <div className="space-y-8">
            {/* Top Bar */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
                        <Briefcase size={24} />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Portfolio Management</h2>
                        <p className="text-sm text-gray-500">Manage case studies, showcase projects, and client deliverables.</p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Search projects..."
                            className="pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 transition-all w-64"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <button
                        onClick={() => handleOpenModal()}
                        className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all flex items-center gap-2 text-sm shrink-0"
                    >
                        <Plus size={18} />
                        Add New Project
                    </button>
                </div>
            </div>

            {/* Category Filter Pills Bar */}
            <div className="bg-white p-3.5 md:p-4 rounded-3xl shadow-sm border border-gray-200/80 flex flex-nowrap items-center justify-start gap-1.5 sm:gap-2 overflow-x-auto no-scrollbar">
                {categories.map((category) => {
                    const active = selectedCategory === category;
                    return (
                        <button
                            key={category}
                            onClick={() => setSelectedCategory(category)}
                            className={`px-3 py-1.5 sm:px-3.5 sm:py-1.5 text-xs sm:text-sm font-semibold rounded-full whitespace-nowrap transition-all duration-200 flex-shrink-0 flex items-center gap-1.5 ${
                                active
                                    ? 'bg-[#0B132A] text-white shadow-md'
                                    : 'bg-slate-100 hover:bg-slate-200/80 text-slate-700'
                            }`}
                        >
                            {getCategoryIcon(category, active)}
                            <span>{category}</span>
                        </button>
                    );
                })}
            </div>

            {/* Portfolio Grid */}
            {loading ? (
                <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-gray-100">
                    <Loader2 className="animate-spin text-blue-600 mb-3" size={32} />
                    <p className="text-sm text-gray-500">Loading portfolio projects...</p>
                </div>
            ) : filteredItems.length === 0 ? (
                <div className="bg-white p-12 rounded-2xl border border-gray-100 text-center">
                    <Briefcase className="mx-auto text-gray-300 mb-3" size={48} />
                    <h3 className="text-lg font-bold text-gray-700">No Portfolio Projects Found</h3>
                    <p className="text-sm text-gray-500 mt-1">Get started by adding your first showcase project.</p>
                    <button
                        onClick={() => handleOpenModal()}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
                    >
                        <Plus size={16} /> Add Project
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredItems.map(item => {
                        const displayImg = resolvePortfolioImage(item.title, item.image_url);
                        return (
                            <div key={item.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col hover:shadow-md transition-all">
                                <div className="relative h-48 bg-slate-50 border-b border-gray-100 p-2 flex items-center justify-center overflow-hidden group">
                                    <img
                                        src={displayImg}
                                        alt={item.title}
                                        className="max-h-full max-w-full w-auto h-auto object-contain group-hover:scale-105 transition-transform duration-300 rounded-lg"
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).src = resolvePortfolioImage(item.title);
                                        }}
                                    />
                                    <span className="absolute top-3 left-3 px-3 py-1 bg-blue-900/80 backdrop-blur-md text-white text-xs font-semibold rounded-full">
                                        {item.category}
                                    </span>
                                </div>

                            <div className="p-6 flex-1 flex flex-col justify-between space-y-4">
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                                    <p className="text-xs text-gray-600 line-clamp-3 leading-relaxed">
                                        {item.description}
                                    </p>
                                </div>

                                <div className="pt-4 border-t border-gray-100 flex items-center justify-between">
                                    {item.demo_url ? (
                                        <a
                                            href={item.demo_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1"
                                        >
                                            Demo Link <ExternalLink size={12} />
                                        </a>
                                    ) : (
                                        <span className="text-xs text-gray-400">No Demo URL</span>
                                    )}

                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => handleOpenModal(item)}
                                            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                            title="Edit Project"
                                        >
                                            <Edit2 size={16} />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(item.id, item.title)}
                                            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                            title="Delete Project"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
                </div>
            )}

            {/* Create / Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 overflow-hidden">
                    <div
                        className="absolute inset-0 bg-blue-950/60 backdrop-blur-sm transition-opacity"
                        onClick={() => setShowModal(false)}
                    ></div>
                    <div className="relative bg-white w-full max-w-lg max-h-[90vh] flex flex-col rounded-2xl shadow-2xl overflow-hidden animate-fade-in-up">
                        {/* Header */}
                        <div className="bg-gradient-to-r from-blue-900 to-indigo-900 px-6 py-4 flex items-center justify-between text-white shrink-0">
                            <div className="flex items-center gap-2">
                                <Briefcase size={20} className="text-blue-300" />
                                <h3 className="font-bold">{editingItem ? 'Edit Portfolio Project' : 'Add New Portfolio Project'}</h3>
                            </div>
                            <button
                                onClick={() => setShowModal(false)}
                                className="p-1 hover:bg-white/10 rounded-lg text-blue-200 hover:text-white transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Scrollable Form */}
                        <form id="portfolio-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4 no-scrollbar">
                            <div>
                                <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1">Project Title</label>
                                <input
                                    type="text"
                                    required
                                    placeholder="e.g. Capitaline Database"
                                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">Project Category</label>
                                <div className="space-y-2">
                                    <select
                                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all font-medium text-gray-700"
                                        value={PRESET_CATEGORIES.includes(formData.category) ? formData.category : 'Custom'}
                                        onChange={(e) => {
                                            if (e.target.value !== 'Custom') {
                                                setFormData({ ...formData, category: e.target.value });
                                            }
                                        }}
                                    >
                                        {PRESET_CATEGORIES.map(cat => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                        <option value="Custom">+ Custom Category...</option>
                                    </select>

                                    {(!PRESET_CATEGORIES.includes(formData.category) || formData.category === '') && (
                                        <input
                                            type="text"
                                            required
                                            placeholder="Enter custom category name (e.g. E-Commerce, Mobile App)"
                                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                                            value={formData.category}
                                            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                        />
                                    )}

                                    {/* Quick Category Chips */}
                                    <div className="flex flex-wrap gap-1.5 pt-1">
                                        <span className="text-[10px] text-gray-400 font-medium self-center mr-1">Quick Select:</span>
                                        {PRESET_CATEGORIES.map((cat) => (
                                            <button
                                                key={cat}
                                                type="button"
                                                onClick={() => setFormData({ ...formData, category: cat })}
                                                className={`text-[11px] px-2.5 py-1 rounded-lg border transition-all flex items-center gap-1.5 ${
                                                    formData.category === cat
                                                        ? 'bg-indigo-600 text-white border-indigo-600 font-semibold'
                                                        : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                                                }`}
                                            >
                                                {getCategoryIcon(cat)}
                                                <span>{cat}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Project Media Selection (Home Image & Video) */}
                            <div className="space-y-3 bg-slate-50 p-4 rounded-2xl border border-slate-200/80">
                                <div className="flex items-center justify-between">
                                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider">
                                        Project Media (Home Image & Video)
                                    </label>
                                    <span className="text-[10px] font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-100">
                                        Independent Uploads
                                    </span>
                                </div>

                                {/* File Inputs */}
                                <input
                                    type="file"
                                    ref={imageInputRef}
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    className="hidden"
                                />
                                <input
                                    type="file"
                                    ref={videoInputRef}
                                    accept="video/*,.mp4,.webm,.mov,.ogg"
                                    onChange={handleVideoUpload}
                                    className="hidden"
                                />

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {/* 1. Home / Cover Image Upload Box */}
                                    <div className="bg-white p-3.5 rounded-xl border border-gray-200/80 flex flex-col justify-between space-y-2">
                                        <div>
                                            <div className="flex items-center gap-1.5 text-xs font-bold text-gray-800 mb-1">
                                                <ImageIcon size={16} className="text-blue-600 shrink-0" />
                                                <span>1. Home / Cover Image</span>
                                            </div>
                                            <p className="text-[11px] text-gray-500 mb-2">
                                                Card image shown on Home showcase
                                            </p>
                                            <div
                                                onClick={() => imageInputRef.current?.click()}
                                                className="border-2 border-dashed border-blue-200 hover:border-blue-500 bg-blue-50/40 hover:bg-blue-50/80 transition-all rounded-xl p-3 text-center cursor-pointer flex flex-col items-center justify-center gap-1 group"
                                            >
                                                <Upload size={16} className="text-blue-600 group-hover:scale-110 transition-transform" />
                                                <span className="text-xs font-bold text-gray-700">Click to Upload Home Image</span>
                                                <span className="text-[10px] text-gray-400">PNG, JPG, WEBP</span>
                                            </div>
                                        </div>
                                        <div>
                                            <input
                                                type="text"
                                                placeholder="Or paste Home Image URL..."
                                                className="w-full px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs outline-none focus:ring-1 focus:ring-blue-500"
                                                value={formData.image_url}
                                                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                                            />
                                        </div>
                                    </div>

                                    {/* 2. Video Upload Box */}
                                    <div className="bg-white p-3.5 rounded-xl border border-gray-200/80 flex flex-col justify-between space-y-2">
                                        <div>
                                            <div className="flex items-center gap-1.5 text-xs font-bold text-gray-800 mb-1">
                                                <Video size={16} className="text-purple-600 shrink-0" />
                                                <span>2. Project Video (Optional)</span>
                                            </div>
                                            <p className="text-[11px] text-gray-500 mb-2">
                                                Optional demo video for video showcase
                                            </p>
                                            <div
                                                onClick={() => videoInputRef.current?.click()}
                                                className="border-2 border-dashed border-purple-200 hover:border-purple-500 bg-purple-50/40 hover:bg-purple-50/80 transition-all rounded-xl p-3 text-center cursor-pointer flex flex-col items-center justify-center gap-1 group"
                                            >
                                                <Video size={16} className="text-purple-600 group-hover:scale-110 transition-transform" />
                                                <span className="text-xs font-bold text-gray-700">Click to Upload Video</span>
                                                <span className="text-[10px] text-gray-400">MP4, WEBM, MOV (Max 10MB or paste Video URL)</span>
                                            </div>
                                        </div>
                                        <div>
                                            <input
                                                type="text"
                                                placeholder="Or paste Video URL..."
                                                className="w-full px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs outline-none focus:ring-1 focus:ring-purple-500"
                                                value={formData.video_url}
                                                onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Prompt when video is present but Home Image is missing */}
                                {formData.video_url && !formData.image_url && (
                                    <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between gap-2">
                                        <p className="text-xs text-amber-800 font-medium">
                                            💡 You have uploaded a video! Upload a <strong>Home Image</strong> above to use as the cover thumbnail.
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => imageInputRef.current?.click()}
                                            className="px-2.5 py-1 bg-amber-600 text-white text-xs font-bold rounded-lg hover:bg-amber-700 transition-colors shrink-0"
                                        >
                                            Upload Home Image
                                        </button>
                                    </div>
                                )}

                                {/* Quick Image Presets */}
                                <div className="flex flex-wrap gap-1.5 items-center pt-1">
                                    <span className="text-[10px] text-gray-400 font-medium mr-1">Preset Home Images:</span>
                                    {DEFAULT_PRESET_IMAGES.map((preset) => (
                                        <button
                                            key={preset.path}
                                            type="button"
                                            onClick={() => setFormData({ ...formData, image_url: preset.path })}
                                            className={`text-[11px] px-2 py-0.5 rounded-lg border transition-all ${
                                                formData.image_url === preset.path
                                                    ? 'bg-blue-600 text-white border-blue-600 font-semibold'
                                                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-100'
                                            }`}
                                        >
                                            {preset.name}
                                        </button>
                                    ))}
                                </div>

                                {/* Live Media Previews */}
                                {(formData.image_url || formData.video_url) && (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                                        {/* Home Image Preview */}
                                        {formData.image_url && !isVideoFile(formData.image_url) && (
                                            <div className="p-2 bg-white border border-gray-200 rounded-xl flex items-center justify-between gap-2">
                                                <div className="flex items-center gap-2 overflow-hidden">
                                                    <img
                                                        src={resolvePortfolioImage(formData.title, formData.image_url)}
                                                        alt="Home Preview"
                                                        className="w-10 h-10 object-cover rounded-lg border border-gray-200 shrink-0"
                                                        onError={(e) => {
                                                            (e.target as HTMLImageElement).src = resolvePortfolioImage(formData.title);
                                                        }}
                                                    />
                                                    <div className="truncate text-xs">
                                                        <p className="font-bold text-gray-700">Home Cover Image</p>
                                                        <p className="text-gray-400 text-[10px] truncate max-w-[120px]">
                                                            {formData.image_url.startsWith('data:') ? 'Uploaded Image File' : formData.image_url}
                                                        </p>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => setFormData({ ...formData, image_url: '' })}
                                                    className="p-1 text-gray-400 hover:text-red-500 rounded-lg"
                                                    title="Remove Home Image"
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        )}

                                        {/* Video Preview */}
                                        {(formData.video_url || isVideoFile(formData.image_url)) && (
                                            <div className="p-2 bg-white border border-gray-200 rounded-xl flex items-center justify-between gap-2">
                                                <div className="flex items-center gap-2 overflow-hidden">
                                                    <video
                                                        src={formData.video_url || formData.image_url}
                                                        controls
                                                        className="w-12 h-10 object-cover rounded-lg border border-gray-300 shrink-0 bg-gray-100"
                                                    />
                                                    <div className="truncate text-xs">
                                                        <p className="font-bold text-gray-700">Project Video</p>
                                                        <p className="text-gray-400 text-[10px] truncate max-w-[120px]">
                                                            {(formData.video_url || formData.image_url).startsWith('data:') ? 'Uploaded Video File' : (formData.video_url || formData.image_url)}
                                                        </p>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => setFormData({ ...formData, video_url: '' })}
                                                    className="p-1 text-gray-400 hover:text-red-500 rounded-lg"
                                                    title="Remove Video"
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1">Demo / External Link (Optional)</label>
                                <input
                                    type="url"
                                    placeholder="https://example.com"
                                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                                    value={formData.demo_url}
                                    onChange={(e) => setFormData({ ...formData, demo_url: e.target.value })}
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1">Description</label>
                                <textarea
                                    required
                                    rows={3}
                                    placeholder="Provide detailed description of the project deliverables and key outcomes..."
                                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all resize-none"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                ></textarea>
                            </div>
                        </form>

                        {/* Fixed Footer */}
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3 shrink-0">
                            <button
                                type="button"
                                onClick={() => setShowModal(false)}
                                className="px-5 py-2.5 bg-gray-200/80 text-gray-700 text-sm font-semibold rounded-xl hover:bg-gray-200 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                form="portfolio-form"
                                disabled={submitting}
                                className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-semibold rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all flex items-center gap-2"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" /> Saving...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle2 size={16} /> Save Project
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PortfolioManagement;
